import React, { useEffect, useState } from "react";
import { fetchCourses, mediaUrl } from "../lib/api";
import { GraduationCap, Clock, ArrowUpRight, X, Check } from "lucide-react";
import { useScrollReveal } from "../hooks/useScrollReveal";

const FALLBACK_COURSES = [
  {
    id: "fallback-course-1",
    title: "AI Automation 101",
    level: "Beginner",
    duration: "4 weeks",
    summary: "From zero to your first production agent.",
    order: 1,
  },
  {
    id: "fallback-course-2",
    title: "Build with n8n + LLMs",
    level: "Intermediate",
    duration: "6 weeks",
    summary: "Wire LLMs into real-world workflows.",
    order: 2,
  },
  {
    id: "fallback-course-3",
    title: "Agentic Systems Bootcamp",
    level: "Advanced",
    duration: "8 weeks",
    summary: "Multi-agent architectures, evals, observability.",
    order: 3,
  },
];

export const Courses = ({ onOpenLead }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const revealRef = useScrollReveal(".in-view", [items]);

  useEffect(() => {
    let active = true;

    const load = () => {
      fetchCourses()
        .then((d) => {
          if (!active) return;
          const fetched = d && (Array.isArray(d.items) ? d.items : (Array.isArray(d) ? d : null));
          setItems(fetched && fetched.length > 0 ? fetched : FALLBACK_COURSES);
        })
        .catch(() => {
          if (!active) return;
          setItems((prev) => (Array.isArray(prev) && prev.length > 0 ? prev : FALLBACK_COURSES));
        })
        .finally(() => {
          if (!active) return;
          setLoading(false);
        });
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  const courseList = Array.isArray(items) ? items : FALLBACK_COURSES;

  return (
    <div ref={revealRef} className="pt-28 pb-10" data-testid="courses-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10">
        <p className="text-xs tracking-[0.3em] uppercase text-[#00F0FF] font-bold">
          Courses
        </p>
        <h1 className="font-display text-5xl md:text-7xl font-black text-white mt-4 leading-[0.9] tracking-tighter">
          Learn to <span className="text-gradient-neon">build</span><br />the future.
        </h1>
        <p className="max-w-2xl text-zinc-400 mt-6 text-base">
          Hands-on, project-based programs taught by the same engineers who
          ship our client work. No theory dumps.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-64 rounded-3xl glass animate-pulse"
                />
              ))
            : courseList.map((c) => (
                <div
                  key={c.id}
                  data-testid={`course-card-${c.id}`}
                  onClick={() => setSelectedCourse(c)}
                  className="in-view group glass rounded-3xl p-7 hover:border-[#00F0FF]/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] font-bold">
                      {c.level}
                    </span>
                    <GraduationCap
                      className="w-5 h-5 text-zinc-400 group-hover:text-[#00F0FF] transition-colors"
                      strokeWidth={1.6}
                    />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white mt-5 leading-tight">
                    {c.title}
                  </h3>
                  <p className="text-zinc-400 mt-2 text-sm leading-relaxed line-clamp-3">
                    {c.summary}
                  </p>
                  <div className="mt-6 flex items-center gap-3 text-zinc-400 text-xs">
                    <Clock className="w-4 h-4" />
                    <span>{c.duration}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenLead?.();
                    }}
                    data-testid={`course-enroll-${c.id}`}
                    className="mt-6 w-full bg-white/5 hover:bg-[#00F0FF] hover:text-black border border-white/10 hover:border-[#00F0FF] text-white py-2.5 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
                  >
                    Enroll interest <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
        </div>
      </section>

      {/* Course Details Modal */}
      {selectedCourse && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={() => setSelectedCourse(null)}
          />
          <div className="relative w-full max-w-lg overflow-y-auto glass-strong rounded-3xl p-8 md:p-10 shadow-[0_0_80px_rgba(0,240,255,0.18)]">
            {/* Close Button */}
            <button
              onClick={() => setSelectedCourse(null)}
              className="absolute top-6 right-6 text-zinc-400 hover:text-white"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Label */}
            <div className="flex items-center gap-2 mb-4">
              <GraduationCap className="w-5 h-5 text-[#00F0FF]" />
              <span className="text-xs tracking-[0.2em] uppercase text-[#00F0FF] font-bold">
                {selectedCourse.level} Course
              </span>
            </div>

            {/* Course Title */}
            <h3 className="font-display text-3xl font-black text-white leading-tight">
              {selectedCourse.title}
            </h3>

            {/* Info Badges */}
            <div className="mt-4 flex items-center gap-4 text-zinc-400 text-sm">
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-[#00F0FF]" />
                <span>{selectedCourse.duration}</span>
              </div>
            </div>

            {/* Large Image (if present) */}
            {selectedCourse.image_url && (
              <div className="w-full aspect-video rounded-2xl overflow-hidden border border-white/10 mt-6 bg-black/40">
                <img
                  src={mediaUrl(selectedCourse.image_url)}
                  alt={selectedCourse.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Summary details */}
            <div className="mt-6 border-t border-white/10 pt-6">
              <h4 className="text-xs tracking-wider uppercase text-zinc-400 font-bold mb-3">
                Course Summary
              </h4>
              <p className="text-zinc-300 text-sm leading-relaxed">
                {selectedCourse.summary}
              </p>
            </div>

            {/* Action button */}
            <button
              onClick={() => {
                setSelectedCourse(null);
                onOpenLead?.();
              }}
              className="w-full mt-8 bg-[#00F0FF] text-black font-bold py-4 rounded-xl hover:bg-[#00C8E0] transition-colors flex items-center justify-center gap-2"
            >
              Enroll interest <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;
